// §11.1 — the File Explorer's pure logic, kept out of the component so it can be tested
// without a DOM: ordering, hidden entries, collision-free names, breadcrumbs and range select.
import {
  ROOT_APPS,
  basename,
  isUnder,
  joinVfs,
  parentOf,
  type FileEntry,
  type VfsPath,
} from '@hangar/shared';

// §7.7's import and §7.4's conflict copies need the same rule, so it lives in `shared` and is
// re-exported here for the callers that think of it as an Explorer concern.
export { splitName, uniqueName } from '@hangar/shared';

export type SortKey = 'name' | 'size' | 'modified';
export type ViewMode = 'list' | 'grid' | 'column';

export interface Crumb {
  label: string;
  path: VfsPath;
}

/**
 * Dotfiles are hidden by default. `/Users/<user>/.Trash` is one of them (§7.1), and the Recycle
 * Bin is a *place* with restore semantics — browsing it as a folder full of `%2F`-encoded names
 * would be worse than useless.
 */
export function isHidden(name: string): boolean {
  return name.startsWith('.');
}

/** Directories always come first; the sort key only orders within each group. */
export function sortEntries(
  entries: readonly FileEntry[],
  key: SortKey,
  ascending = true,
): FileEntry[] {
  const direction = ascending ? 1 : -1;
  return [...entries].sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    if (key === 'size' && !a.isDir && !b.isDir && a.size !== b.size) {
      return (a.size - b.size) * direction;
    }
    if (key === 'modified' && a.mtime !== b.mtime) return (a.mtime - b.mtime) * direction;
    return a.name.localeCompare(b.name, undefined, { numeric: true }) * direction;
  });
}

/**
 * The trail from the place `path` sits in down to `path` itself. It stops at Home or `/Apps`
 * because those are the roots §9.5 gives the file manager — a crumb for `/` would be a button
 * that can only fail. Labels are path segments, never display text: what the root crumb is
 * *called* is the component's business, because that string belongs in `copy.ts`.
 */
export function crumbsFor(path: VfsPath, home: VfsPath): Crumb[] {
  const root = isUnder(path, home) ? home : isUnder(path, ROOT_APPS) ? ROOT_APPS : '/';
  const crumbs: Crumb[] = [{ label: basename(root), path: root }];
  if (path === root) return crumbs;

  const rest = path.slice(root === '/' ? 1 : root.length + 1).split('/');
  let walked = root;
  for (const segment of rest) {
    walked = joinVfs(walked, segment);
    crumbs.push({ label: segment, path: walked });
  }
  return crumbs;
}

/** Every directory from `root` down to `path`, for the column view. */
export function chainFor(path: VfsPath, home: VfsPath): VfsPath[] {
  return crumbsFor(path, home).map((crumb) => crumb.path);
}

/** The inclusive span between two rows, in the order they are displayed. */
export function selectRange(order: readonly VfsPath[], anchor: VfsPath, target: VfsPath): VfsPath[] {
  const from = order.indexOf(anchor);
  const to = order.indexOf(target);
  if (from < 0 || to < 0) return [target];
  return order.slice(Math.min(from, to), Math.max(from, to) + 1);
}

/**
 * Would moving `source` into `directory` swallow the destination? A folder cannot be moved into
 * itself or into anything beneath it, and neither can it be "moved" to where it already is.
 */
export function canMoveInto(source: VfsPath, directory: VfsPath): boolean {
  if (parentOf(source) === directory) return false;
  return !isUnder(directory, source);
}

const UNITS = ['B', 'kB', 'MB', 'GB', 'TB'] as const;

/** Decimal units, matching the Settings readout — browsers report quotas the same way. */
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const power = Math.min(UNITS.length - 1, Math.floor(Math.log10(bytes) / 3));
  const value = bytes / 1000 ** power;
  return `${value.toFixed(power === 0 || value >= 100 ? 0 : 1)} ${UNITS[power] ?? 'B'}`;
}

export function formatWhen(mtime: number): string {
  if (!mtime) return '—';
  return new Date(mtime).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The status-bar summary: how many things are here, and how many are picked. */
export function summarize(entries: readonly FileEntry[], selected: number): string {
  const items = entries.length === 1 ? '1 item' : `${entries.length} items`;
  return selected > 0 ? `${items}, ${selected} selected` : items;
}
